import { useCallback, useState } from 'react';
import noop from 'lodash/noop';
import { fetchQuery, type RefetchFnDynamic } from 'react-relay';
import type { Disposable } from 'relay-runtime';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data$key as ChildIssueTableRefetchableFragment } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data.graphql';
import type ConfigurableChildIssuesPanelWithFragmentQuery from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery.graphql';
import type {
	ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery as ChildIssueTableRefetchableQuery,
	ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery$variables as ChildIssueTableRefetchableQueryVariables,
} from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery.graphql';

type Refetch = RefetchFnDynamic<
	ChildIssueTableRefetchableQuery,
	ChildIssueTableRefetchableFragment
>;

interface UseRefetchWithoutSuspendProps {
	defaultVariables: Partial<ChildIssueTableRefetchableQueryVariables>;
	refetch: Refetch;
	query: typeof ConfigurableChildIssuesPanelWithFragmentQuery;
	onError?: (e: Error) => void;
}

export const useRefetchWithoutSuspend = ({
	defaultVariables,
	refetch,
	query,
	onError,
}: UseRefetchWithoutSuspendProps) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const refetchWithoutSuspending: Refetch = useCallback(
		(overrideVariables) => {
			setIsLoading(true);
			let disposable: Disposable = { dispose: noop };
			const combinedVariables = { ...defaultVariables, ...overrideVariables };
			fetchQuery(getRelayEnvironment(), query, combinedVariables).subscribe({
				complete: () => {
					setIsLoading(false);
					disposable = refetch(combinedVariables, { fetchPolicy: 'store-only' });
				},
				error: (e: Error) => {
					onError?.(e);
					setIsLoading(false);
				},
			});
			return disposable;
		},
		[defaultVariables, query, refetch, onError],
	);
	return { isLoading, refetchWithoutSuspending };
};
